import { useCallback, useMemo } from 'react'
import { useAuthStore } from '@/store/useAuthStore'
import { updateUserProfile } from '@/services/api/users'
import {
  resolveOnboardingSteps,
  getOnboardingProgress,
  shouldShowOnboarding,
  isOnboardingComplete,
} from '@/utils/onboarding'
import type { OnboardingState } from '@/types/user'

/**
 * Reads the live onboarding state off the auth store and persists the user's
 * manual choices (skip / finish / tick-off) to their profile. Auto-detected
 * done-ness is always derived fresh from the profile via the pure resolver, so
 * this hook only ever writes the small `onboarding` map.
 */
export function useOnboarding() {
  const profile = useAuthStore((s) => s.profile)
  const setProfile = useAuthStore((s) => s.setProfile)

  const steps = useMemo(() => resolveOnboardingSteps(profile), [profile])
  const progress = useMemo(() => getOnboardingProgress(profile), [profile])

  // Persist a patch to profile.onboarding — merges with what's already there so
  // completedSteps/dismissed/completed don't clobber each other.
  const patchOnboarding = useCallback(
    async (patch: Partial<OnboardingState>) => {
      if (!profile) return
      const next: OnboardingState = { ...(profile.onboarding ?? {}), ...patch }
      // Optimistic local update so the UI reacts immediately.
      setProfile({ ...profile, onboarding: next })
      try {
        await updateUserProfile(profile.uid, { onboarding: next })
      } catch (err) {
        console.error('Failed to save onboarding state:', err)
      }
    },
    [profile, setProfile]
  )

  const dismiss = useCallback(() => patchOnboarding({ dismissed: true }), [patchOnboarding])
  const complete = useCallback(() => patchOnboarding({ completed: true }), [patchOnboarding])

  const markStepDone = useCallback(
    (stepId: string) => {
      const existing = profile?.onboarding?.completedSteps ?? []
      if (existing.includes(stepId)) return Promise.resolve()
      return patchOnboarding({ completedSteps: [...existing, stepId] })
    },
    [profile, patchOnboarding]
  )

  return {
    profile,
    steps,
    progress,
    // Auto-open gate for the first-login wizard.
    shouldAutoShow: shouldShowOnboarding(profile),
    isComplete: isOnboardingComplete(profile),
    dismiss,
    complete,
    markStepDone,
  }
}
